import { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, ShieldCheck, CheckCircle2 } from 'lucide-react';

/**
 * DeclaredValueAndCoverage — top-of-form widget for EVERY shipping page.
 *
 *  - "How much is the package worth?" (declared value)
 *  - "Which coverage?" — plain-English options, with no rates and no premium.
 *    Mike, 4-oct: "should not show the price here. Each client has its own
 *    price". Las tasas siguen en COVERAGE_OPTIONS para quien las use por
 *    codigo; la pantalla ya no las muestra.
 *
 * Props:
 *   declaredValue      number
 *   onDeclaredChange   (number) => void
 *   coverage           'none' | 'A' | 'B' | 'C' | 'D'
 *   onCoverageChange   (string) => void
 */
const COVERAGE_OPTIONS = [
  {
    id: 'none',
    name: 'No coverage',
    tagline: 'Ship at your own risk',
    covers: [],
  },
  {
    id: 'A',
    name: 'A · Shipping Insurance Full',
    tagline: '0.20% of declared value',
    rate: 0.0020,
    covers: ['Full shipping loss coverage', 'Full shipping damage coverage'],
    tierCode: 'A',
  },
  {
    id: 'B',
    name: 'B · Shipping Insurance Shared Risk',
    tagline: '0.12% of declared value',
    rate: 0.0012,
    covers: ['Shipping loss & damage (shared risk)', 'Merchant retains small deductible'],
    tierCode: 'B',
  },
  {
    id: 'C',
    name: 'C · Shipping + Chargeback Coverage',
    tagline: '1.00% of declared value',
    rate: 0.0100,
    covers: ['Shipping loss & damage', 'Chargeback protection'],
    tierCode: 'C',
  },
  {
    id: 'D',
    name: 'D · Full Benefits & Coverages',
    tagline: '1.25% of declared value',
    rate: 0.0125,
    covers: [
      'Shipping loss & damage',
      'Chargeback protection',
      'Porch piracy · Shortage · Return · Customs',
    ],
    tierCode: 'D',
    badge: 'Most protected',
  },
];

export default function DeclaredValueAndCoverage({
  declaredValue,
  onDeclaredChange,
  coverage = 'A',
  onCoverageChange,
}) {
  // Auto-emit a default so parent has a coverage plan even before user clicks
  useEffect(() => {
    if (!coverage) onCoverageChange && onCoverageChange('A');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = COVERAGE_OPTIONS.find(o => o.id === coverage) || COVERAGE_OPTIONS[1];

  return (
    <Card data-testid="declared-coverage-card" className="border-emerald-200 dark:border-emerald-900/50">
      <CardContent className="pt-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> How much is the package worth?
            </Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={declaredValue || ''}
              onChange={(e) => onDeclaredChange && onDeclaredChange(Number(e.target.value) || 0)}
              placeholder="Declared value in USD"
              className="text-lg font-semibold"
              data-testid="declared-value-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Coverage
            </Label>
            <select
              value={coverage}
              onChange={(e) => onCoverageChange && onCoverageChange(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-medium"
              data-testid="coverage-select"
            >
              {COVERAGE_OPTIONS.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selected.id !== 'none' && (
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-3 border border-emerald-100 dark:border-emerald-900/40">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
                {selected.name}
                {selected.badge && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-emerald-600 text-white px-2 py-0.5 rounded-full">{selected.badge}</span>
                )}
              </span>
            </div>
            <ul className="space-y-0.5">
              {selected.covers.map(c => (
                <li key={c} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { COVERAGE_OPTIONS };
